// Connect wallets, one per chain family: EVM (EIP-6963), Solana (wallet standard, through the
// wallet adapter), Sui (wallet standard), and the Jupnet key this browser holds.

import { useState } from "react";
import type { WalletContextState } from "@solana/wallet-adapter-react";
import { WalletReadyState } from "@solana/wallet-adapter-base";
import type { Wallet as SuiWallet, WalletAccount as SuiAccount } from "@mysten/wallet-standard";
import { Keypair } from "@solana/web3.js";
import type { Family } from "../chains";
import { connectEvm, type EvmWalletInfo } from "../evm";
import { short } from "../format";
import { adoptJupnetKey, exportJupnetKey, importJupnetKey } from "../jupnet-key";
import { connectSui } from "../sui";
import { ChainLogo, IconCheck, IconCopy, IconKey } from "./icons";
import { Modal } from "./Modal";

export type EvmConn = { info: EvmWalletInfo; address: string };
export type SuiConn = { wallet: SuiWallet; account: SuiAccount };

export const FAMILY_NAME: Record<Family, string> = { evm: "EVM wallet", solana: "Solana wallet", sui: "Sui wallet", jupnet: "Jupnet key" };

/** Copy `text`; false when the clipboard is unavailable or refused (the caller shows it instead). */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function CopyButton({ text, label = "Copy", className = "link-btn" }: { text: string; label?: string; className?: string }) {
  const [done, setDone] = useState<boolean | null>(null);
  return (
    <>
      <button
        className={className}
        onClick={async () => {
          const ok = await copyText(text);
          setDone(ok);
          if (ok) setTimeout(() => setDone(null), 1500);
        }}
        aria-label={`${label}: ${text}`}
        title={text}
      >
        {done ? <IconCheck size={14} /> : <IconCopy size={14} />} {done ? "Copied" : label}
      </button>
      {done === false && <input className="copy-fallback mono" readOnly value={text} onFocus={(e) => e.currentTarget.select()} aria-label="Copy this by hand" />}
    </>
  );
}

type Props = {
  focus?: Family | null;
  evmWallets: EvmWalletInfo[];
  evm: EvmConn | null;
  setEvm: (c: EvmConn | null) => void;
  sol: WalletContextState;
  suiWallets: SuiWallet[];
  sui: SuiConn | null;
  setSui: (c: SuiConn | null) => void;
  jupnetKey: Keypair | null;
  setJupnetKey: (k: Keypair | null, stored: boolean) => void;
  jupnetSource: boolean;
  /** a warning about the Jupnet key the page keeps showing (the key may live only in this tab) */
  onKeyWarning: (w: string | null) => void;
  onClose: () => void;
};

export function Wallets(p: Props) {
  const [err, setErr] = useState<string | null>(null);
  const rejected = (e: any) => e?.code === 4001 || /reject|denied|cancel/i.test(String(e?.message ?? e));
  const fail = (e: any) => setErr(rejected(e) ? "You rejected the connection in your wallet." : `Could not connect: ${e?.message ?? e}`);
  const solWallets = p.sol.wallets.filter((w) => w.readyState === WalletReadyState.Installed || w.readyState === WalletReadyState.Loadable);
  const solAddr = p.sol.publicKey?.toBase58();

  return (
    <Modal title="Wallets" onClose={p.onClose}>
      {err && (
        <p className="alert error" role="alert">
          {err}
        </p>
      )}
      <Section family="evm" chains={["ethereum", "base", "arbitrum", "bsc", "robinhood"]} title="EVM" focus={p.focus}>
        {p.evm ? (
          <Connected icon={p.evm.info.icon} name={p.evm.info.name} address={p.evm.address} onDisconnect={() => p.setEvm(null)} />
        ) : p.evmWallets.length ? (
          <ul className="wallet-list">
            {p.evmWallets.map((w) => (
              <li key={w.uuid}>
                <button
                  className="wallet-row"
                  onClick={async () => {
                    setErr(null);
                    try {
                      p.setEvm({ info: w, address: await connectEvm(w.provider) });
                    } catch (e) {
                      fail(e);
                    }
                  }}
                >
                  {w.icon ? <img src={w.icon} alt="" /> : <span className="wallet-ph" />} {w.name}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="fine">No EVM wallet found in this browser. Install one (MetaMask, Rabby, Coinbase Wallet, …) and reload.</p>
        )}
      </Section>

      <Section family="solana" chains={["solana"]} title="Solana" focus={p.focus}>
        {p.sol.connected && solAddr ? (
          <Connected icon={p.sol.wallet?.adapter.icon} name={p.sol.wallet?.adapter.name ?? "Solana wallet"} address={solAddr} onDisconnect={() => p.sol.disconnect().catch(() => {})} />
        ) : solWallets.length ? (
          <ul className="wallet-list">
            {solWallets.map((w) => (
              <li key={w.adapter.name}>
                <button
                  className="wallet-row"
                  disabled={p.sol.connecting}
                  onClick={() => {
                    setErr(null);
                    if (p.sol.wallet?.adapter.name === w.adapter.name) p.sol.connect().catch(fail);
                    else p.sol.select(w.adapter.name); // the provider connects the wallet selected
                  }}
                >
                  <img src={w.adapter.icon} alt="" /> {w.adapter.name}
                  {p.sol.connecting && p.sol.wallet?.adapter.name === w.adapter.name && <span className="muted"> connecting…</span>}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="fine">No Solana wallet found in this browser. Install one (Phantom, Solflare, Backpack, …) and reload.</p>
        )}
      </Section>

      <Section family="sui" chains={["sui"]} title="Sui" focus={p.focus}>
        {p.sui ? (
          <Connected icon={p.sui.wallet.icon} name={p.sui.wallet.name} address={p.sui.account.address} onDisconnect={() => p.setSui(null)} />
        ) : p.suiWallets.length ? (
          <ul className="wallet-list">
            {p.suiWallets.map((w) => (
              <li key={w.name}>
                <button
                  className="wallet-row"
                  onClick={async () => {
                    setErr(null);
                    try {
                      p.setSui({ wallet: w, account: await connectSui(w) });
                    } catch (e) {
                      fail(e);
                    }
                  }}
                >
                  {w.icon && <img src={w.icon} alt="" />} {w.name}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="fine">No Sui wallet found in this browser. Install one (Slush, Suiet, Phantom, …) and reload.</p>
        )}
      </Section>

      <Section family="jupnet" chains={["jupnet"]} title="Jupnet" focus={p.focus}>
        <JupnetKey current={p.jupnetKey} onChange={p.setJupnetKey} source={p.jupnetSource} onWarning={p.onKeyWarning} />
      </Section>
    </Modal>
  );
}

function Section({ family, chains, title, focus, children }: { family: Family; chains: Parameters<typeof ChainLogo>[0]["chain"][]; title: string; focus?: Family | null; children: React.ReactNode }) {
  return (
    <section className={`wallet-section${focus === family ? " focus" : ""}`} aria-label={title}>
      <div className="wallet-section-head">
        <span className="logo-stack">
          {chains.map((c) => (
            <ChainLogo key={c} chain={c} size={18} />
          ))}
        </span>
        <h3>{title}</h3>
      </div>
      {children}
    </section>
  );
}

function Connected({ icon, name, address, onDisconnect }: { icon?: string; name: string; address: string; onDisconnect: () => void }) {
  return (
    <div className="wallet-connected">
      {icon ? <img src={icon} alt="" /> : <span className="wallet-ph" />}
      <span className="wallet-connected-main">
        <span>{name}</span>
        <span className="mono muted">{short(address, 6)}</span>
      </span>
      <CopyButton text={address} />
      <button className="link-btn" onClick={onDisconnect}>
        Disconnect
      </button>
    </div>
  );
}

/** The Jupnet key: generate one, import a secret, back it up, or forget it. Kept in this browser's
 *  storage (a demo convenience); never overwrites a key another tab stored. */
function JupnetKey({ current, onChange, source, onWarning }: { current: Keypair | null; onChange: (k: Keypair | null, stored: boolean) => void; source: boolean; onWarning: (w: string | null) => void }) {
  // its own slot, cleared only by the next key action: the key (and its funds) is gone on reload
  const [warning, setWarningHere] = useState<string | null>(null);
  const setWarning = (w: string | null) => {
    setWarningHere(w);
    onWarning(w);
  };
  const [importing, setImporting] = useState(false);
  const [secret, setSecret] = useState("");
  const [importErr, setImportErr] = useState<string | null>(null);
  const [forgetting, setForgetting] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const set = (k: Keypair | null) => {
    const a = adoptJupnetKey(k, current);
    setWarning(a.warning);
    onChange(a.key, a.stored);
  };
  const warned = warning && (
    <p className="alert warn" role="alert">
      {warning}
    </p>
  );

  if (!current)
    return (
      <div className="jupnet-key">
        <p className="fine">
          A Jupnet key is an account this browser holds: it receives what you buy on Jupnet{source ? ", and signs swaps paid from Jupnet (the api pays the fee)" : ""}. Back it up — it lives only here.
        </p>
        {importing ? (
          <form
            className="import"
            onSubmit={(e) => {
              e.preventDefault();
              try {
                set(importJupnetKey(secret));
                setSecret("");
                setImporting(false);
                setImportErr(null);
              } catch (er) {
                setImportErr((er as Error).message);
              }
            }}
          >
            <textarea className="mono" rows={3} value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Secret key: base58, or the JSON byte array the CLI writes" aria-label="Jupnet secret key" autoFocus spellCheck={false} autoComplete="off" />
            {importErr && <p className="field-error">{importErr}</p>}
            <div className="row-btns">
              <button type="button" className="btn ghost" onClick={() => (setImporting(false), setImportErr(null))}>
                Cancel
              </button>
              <button type="submit" className="btn" disabled={!secret.trim()}>
                Import
              </button>
            </div>
          </form>
        ) : (
          <div className="row-btns">
            <button className="btn" onClick={() => set(Keypair.generate())}>
              <IconKey size={16} /> Create Jupnet key
            </button>
            <button className="btn ghost" onClick={() => setImporting(true)}>
              Import
            </button>
          </div>
        )}
        {warned}
      </div>
    );

  const addr = current.publicKey.toBase58();
  return (
    <div className="jupnet-key">
      <div className="wallet-connected">
        <ChainLogo chain="jupnet" size={28} />
        <span className="wallet-connected-main">
          <span>Jupnet key</span>
          <span className="mono muted">{short(addr, 6)}</span>
        </span>
        <CopyButton text={addr} />
      </div>
      <div className="row-btns">
        {revealed ? (
          <>
            <input className="mono secret" readOnly value={exportJupnetKey(current)} onFocus={(e) => e.currentTarget.select()} aria-label="Your Jupnet secret key" />
            <CopyButton text={exportJupnetKey(current)} label="Copy secret" className="btn ghost" />
            <button className="btn ghost" onClick={() => setRevealed(false)}>
              Hide
            </button>
          </>
        ) : (
          <button className="btn ghost" onClick={() => setRevealed(true)}>
            Back up secret key
          </button>
        )}
        {forgetting ? (
          <span className="confirm">
            <span className="fine">Funds and refunds on Jupnet belong to this key. Backed it up?</span>
            <button className="btn danger" onClick={() => (setForgetting(false), set(null))}>
              Forget key
            </button>
            <button className="btn ghost" onClick={() => setForgetting(false)}>
              Keep
            </button>
          </span>
        ) : (
          <button className="btn ghost" onClick={() => setForgetting(true)}>
            Forget
          </button>
        )}
      </div>
      {warned}
    </div>
  );
}
